import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { customerActivityApi, type CustomerConsentState } from './customer-activity'
import { queryKeys } from './query-keys'

/** A customer's follow-up timeline, current enrollment, and consent state
 *  (mock-only). Polls every 15s so a reply or an automation advance surfaces
 *  without a reload. */
export function useCustomerActivity(customerId: number | string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.customerActivity.byCustomer(customerId ?? 'none'),
    queryFn: () => customerActivityApi.activity(Number(customerId)),
    enabled: customerId != null,
    // Poll only while the conversation is live — a converted / stopped /
    // unresponsive thread has nothing arriving every 15s (audit low #54).
    refetchInterval: (query) =>
      query.state.data?.enrollment.status === 'active' ? 15 * 1000 : false,
  })
}

/** Text the customer back from inside ETX — appends to the two-way thread. */
export function useSendCustomerMessage(customerId: number | string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ body, estimateId }: { body: string; estimateId?: number | null }) =>
      customerActivityApi.sendMessage(Number(customerId), body, estimateId),
    onSuccess: () => {
      if (customerId != null) {
        qc.invalidateQueries({
          queryKey: queryKeys.customerActivity.byCustomer(customerId),
        })
      }
      // The inbox row (snippet, waiting-on-you state) must reflect the reply
      // immediately, not on the next poll.
      qc.invalidateQueries({ queryKey: queryKeys.campaigns.all })
    },
  })
}

/** The anti-spam control: mark handled-offline / opted-out (halts the drip)
 *  or resubscribe. Invalidates the customer's activity so the halt shows
 *  immediately. */
export function useSetCustomerConsent(customerId: number | string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ state, reason }: { state: CustomerConsentState; reason?: string }) =>
      customerActivityApi.setConsent(Number(customerId), state, reason),
    onSuccess: () => {
      if (customerId != null) {
        qc.invalidateQueries({
          queryKey: queryKeys.customerActivity.byCustomer(customerId),
        })
      }
      // A stop/resume changes the inbox row's state ("Paused") right away.
      qc.invalidateQueries({ queryKey: queryKeys.campaigns.all })
    },
  })
}
